import React, { useState } from 'react';
import { ChevronDown, Plus, Tag, X, Sparkles } from 'lucide-react';
import { TrelloLabel, LabelSuggestion, STANDARD_PRIORITY_LABELS } from '../types/labels';
import { AILabelAnalyzer } from '../lib/aiLabelAnalysis';

interface LabelSelectorProps {
  availableLabels: TrelloLabel[];
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  aiSuggestions?: LabelSuggestion;
  onCreateLabel?: (name: string, color: string) => Promise<void>;
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

const LABEL_COLORS = [
  { name: 'green', className: 'bg-green-500' },
  { name: 'yellow', className: 'bg-yellow-500' },
  { name: 'orange', className: 'bg-orange-500' },
  { name: 'red', className: 'bg-red-500' },
  { name: 'purple', className: 'bg-purple-500' },
  { name: 'blue', className: 'bg-blue-500' },
  { name: 'sky', className: 'bg-sky-500' },
  { name: 'lime', className: 'bg-lime-500' },
  { name: 'pink', className: 'bg-pink-500' },
  { name: 'black', className: 'bg-gray-800' }
];

const LabelSelector: React.FC<LabelSelectorProps> = ({
  availableLabels,
  selectedLabels,
  onLabelsChange,
  aiSuggestions,
  onCreateLabel,
  className = '',
  isOpen: externalIsOpen,
  onToggle: externalOnToggle
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('blue');
  const [creating, setCreating] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalOnToggle || ((open: boolean) => setInternalIsOpen(open));

  const selectedLabelObjects = availableLabels.filter(label => 
    selectedLabels.includes(label.name)
  );

  const handleLabelToggle = (labelName: string) => {
    if (selectedLabels.includes(labelName)) {
      onLabelsChange(selectedLabels.filter(name => name !== labelName));
    } else {
      onLabelsChange([...selectedLabels, labelName]);
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim() || !onCreateLabel) return;
    
    setCreating(true);
    try {
      await onCreateLabel(newLabelName.trim(), newLabelColor);
      setNewLabelName('');
      setShowCreateForm(false);
      // Close main dropdown after creating
      if (externalOnToggle) {
        externalOnToggle();
      } else {
        setInternalIsOpen(false);
      }
    } catch (error) {
      console.error('Failed to create label:', error);
    } finally {
      setCreating(false);
    }
  };

  const applyAISuggestion = (labelName: string) => {
    if (!selectedLabels.includes(labelName)) {
      onLabelsChange([...selectedLabels, labelName]);
    }
  };

  const applyPrioritySuggestion = (priority: string) => {
    if (!aiSuggestions?.priority) return;
    
    const priorityLabel = STANDARD_PRIORITY_LABELS.find(
      label => label.priority === priority
    );
    
    if (priorityLabel && !selectedLabels.includes(priorityLabel.name)) {
      onLabelsChange([...selectedLabels, priorityLabel.name]);
    }
  };

  const handleCreateSuggestedLabel = (labelName: string) => {
    if (!selectedLabels.includes(labelName)) {
      onLabelsChange([...selectedLabels, labelName]);
    }
  };

  const getLabelColor = (color: string) => {
    const colorConfig = LABEL_COLORS.find(c => c.name === color);
    return colorConfig?.className || 'bg-gray-400';
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Labels {aiSuggestions && (
          <span className="text-blue-600 text-xs">✨ AI suggestions available</span>
        )}
      </label>

      {/* Show AI Suggestions */}
      {aiSuggestions && (
        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center mb-2">
            <Sparkles className="h-4 w-4 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-800">AI Suggestions</span>
          </div>
          
          <div className="space-y-2">
            {/* Priority Suggestion */}
            {aiSuggestions.priority && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-700">Priority:</span>
                <button
                  onClick={() => applyPrioritySuggestion(aiSuggestions.priority)}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                    aiSuggestions.priority === 'High' ? 'bg-red-100 text-red-800 border-red-200' :
                    aiSuggestions.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                    'bg-green-100 text-green-800 border-green-200'
                  } hover:opacity-80 transition-opacity`}
                >
                  {aiSuggestions.priority} Priority
                  <Plus className="h-3 w-3 ml-1" />
                </button>
              </div>
            )}

            {/* Existing Labels Suggestions */}
            {aiSuggestions.existingLabels && aiSuggestions.existingLabels.length > 0 && (
              <div>
                <span className="text-sm text-blue-700">Suggested labels:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {aiSuggestions.existingLabels.map((labelName) => {
                    const label = availableLabels.find(l => l.name === labelName);
                    if (!label) return null;
                    
                    return (
                      <button
                        key={labelName}
                        onClick={() => handleLabelToggle(labelName)}
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border transition-opacity ${
                          selectedLabels.includes(labelName)
                            ? `bg-${label.color}-100 text-${label.color}-800 border-${label.color}-200`
                            : `bg-${label.color}-50 text-${label.color}-700 border-${label.color}-200 hover:bg-${label.color}-100`
                        }`}
                      >
                        {labelName}
                        <Plus className="h-3 w-3 ml-1" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* New Label Suggestion */}
            {aiSuggestions.suggestedNewLabel && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-700">New label:</span>
                <button
                  onClick={() => handleCreateSuggestedLabel(aiSuggestions.suggestedNewLabel!)}
                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-opacity"
                >
                  {aiSuggestions.suggestedNewLabel}
                  <Plus className="h-3 w-3 ml-1" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected Labels Display - Show actual label names */}
      <button
        onClick={() => {
          if (externalOnToggle) {
            externalOnToggle();
          } else {
            setInternalIsOpen(!internalIsOpen);
          }
        }}
        className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <div className="flex items-center space-x-2">
          <Tag className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-700">
            {selectedLabels.length === 0 ? (
              'Select labels'
            ) : selectedLabels.length === 1 ? (
              <span className="flex items-center space-x-2">
                <span>{selectedLabels[0]}</span>
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">1 label</span>
              </span>
            ) : (
              <span className="flex items-center space-x-2">
                <span>{selectedLabels.slice(0, 2).join(', ')}{selectedLabels.length > 2 ? '...' : ''}</span>
                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{selectedLabels.length} labels</span>
              </span>
            )}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
          {/* Existing Labels */}
          {availableLabels.map((label) => (
            <button
              key={label.id}
              onClick={() => handleLabelToggle(label.name)}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between"
            >
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${getLabelColor(label.color)}`}></span>
                <span className="truncate">{label.name}</span>
              </div>
              {selectedLabels.includes(label.name) && (
                <X className="h-4 w-4 text-gray-400" />
              )}
            </button>
          ))}

          {/* Create New Label Button */}
          {onCreateLabel && (
            <div className="border-t border-gray-200 mt-1 pt-1">
              <button
                onClick={() => {
                  setShowCreateForm(true);
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 hover:bg-indigo-50 flex items-center text-indigo-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create new label
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Label Form */}
      {showCreateForm && (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Create New Label</h4>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Label Name
              </label>
              <input
                type="text"
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                placeholder="Enter label name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Color
              </label>
              <div className="flex flex-wrap gap-1">
                {LABEL_COLORS.map((color) => (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => setNewLabelColor(color.name)}
                    className={`w-6 h-6 rounded-full ${color.className} ${
                      newLabelColor === color.name ? 'ring-2 ring-offset-1 ring-gray-400' : ''
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewLabelName('');
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateLabel}
                disabled={!newLabelName.trim() || creating}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelSelector; 